// Statystyki (owner 2026-10-27, spec docs/superpowers/specs/2026-10-27-player-stats-design.md): the player's own
// lifetime numbers, Minecraft-style — a row of tabs over a card of "{icon} label … number" rows (layout B).
// Opened from Ustawienia (SettingsModal.js); closing returns to the game. The rows come from statsRows.mjs.
// A tab longer than the card scrolls by whole rows (wheel / drag): a geometry mask would not follow the
// touch enlargement (touchEnlarge.js) that scales this layer on phones.
import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { api } from '../api/client.js';
import { lockInput, unlockInput } from './inputLock.js';
import { trackPanel } from './panelSwitch.js';
import { gamePanel, gameButton, woodTitle } from './gameUi.js';
import { FONT } from './gameUiStyle.mjs';
import { iconText } from './iconText.js';
import { audioManager } from '../audio/AudioManager.js';
import { enlargeForTouch } from './touchEnlarge.js';
import { TABS, SINCE_NOTE, statsRows } from './statsRows.mjs';

const W = 640, H = 612;          // ends above the canned-chat bar (DOM, bottom of the screen)
const CARD_W = 580, ROW_H = 34, VISIBLE = 10;
const TAB_H = 38, TAB_GAP = 8;
const TEXT = { fontFamily: FONT, fontSize: '17px', color: '#3b2a1a' };

export function openStats(scene) {
  if (!scene) return;
  const cam = scene.cameras.main;
  const ox = cam.scrollX, oy = cam.scrollY, x = ox + GAME_WIDTH / 2, y = oy + GAME_HEIGHT / 2;
  const top = y - H / 2;
  const layer = scene.add.container(0, 0).setDepth(2000);
  lockInput(scene);
  let closed = false;
  let untrack = () => {};
  const close = () => {
    if (closed) return;
    closed = true;
    untrack();
    scene.input.keyboard?.off('keydown-ESC', onEsc);
    unlockInput(scene);
    layer.destroy();
  };
  const onEsc = () => { audioManager.playSfx('click'); close(); };
  untrack = trackPanel(scene, 'stats', close);
  scene.events.once('shutdown', close);
  scene.input.keyboard?.on('keydown-ESC', onEsc);

  const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0, 0).setInteractive();
  overlay.on('pointerup', () => { audioManager.playSfx('click'); close(); });
  const panel = gamePanel(scene, x, y, W, H);
  panel.setInteractive(new Phaser.Geom.Rectangle(-W / 2, -H / 2, W, H), Phaser.Geom.Rectangle.Contains);
  const title = woodTitle(scene, x, top, 'Statystyki {medal}');
  layer.add([overlay, panel, title]);

  const tabsLayer = scene.add.container(0, 0);
  const body = scene.add.container(0, 0);
  layer.add([tabsLayer, body]);

  let active = TABS[0];
  let tabs = null;          // statsRows(payload) once loaded
  let offset = 0;           // first visible row of a long tab

  // Tabs: small buttons in rows that wrap inside the card's width.
  const tabWidth = (label) => Math.max(60, label.length * 11 + 28);
  const renderTabs = () => {
    tabsLayer.removeAll(true);
    const lines = [[]];
    let used = 0;
    for (const t of TABS) {
      const w = tabWidth(t);
      if (used + w > CARD_W && lines.at(-1).length) { lines.push([]); used = 0; }
      lines.at(-1).push([t, w]);
      used += w + TAB_GAP;
    }
    lines.forEach((line, i) => {
      const total = line.reduce((s, [, w]) => s + w, 0) + TAB_GAP * (line.length - 1);
      let tx = x - total / 2;
      const ty = top + 52 + i * (TAB_H + TAB_GAP) + TAB_H / 2;
      for (const [t, w] of line) {
        tabsLayer.add(gameButton(scene, tx + w / 2, ty, w, TAB_H, t, () => {
          if (active === t) return;
          active = t; offset = 0;
          renderTabs(); renderBody();
        }, { variant: t === active ? 'wood' : 'secondary', fontSize: 15 }));
        tx += w + TAB_GAP;
      }
    });
  };

  const cardTop = top + 52 + 2 * (TAB_H + TAB_GAP) + 6;
  const cardH = VISIBLE * ROW_H + 12;
  const left = x - CARD_W / 2, right = x + CARD_W / 2;

  const message = (text) => {
    body.removeAll(true);
    body.add(iconText(scene, x, cardTop + cardH / 2, text, { ...TEXT, color: '#6b5a3a' }).setOrigin(0.5));
  };

  const renderBody = () => {
    body.removeAll(true);
    const { rows, since } = tabs[active];
    const card = scene.add.graphics();
    card.fillStyle(0xffffff, 1).fillRoundedRect(left, cardTop, CARD_W, cardH, 12);
    card.lineStyle(2, 0xe2c89a, 1).strokeRoundedRect(left, cardTop, CARD_W, cardH, 12);
    body.add(card);
    const shown = rows.slice(offset, offset + VISIBLE);
    shown.forEach((r, i) => {
      const cy = cardTop + 6 + i * ROW_H + ROW_H / 2;
      body.add(iconText(scene, left + 16, cy, `{${r.icon}} ${r.label}`, TEXT).setOrigin(0, 0.5));
      let vx = right - 16;
      if (r.note) {
        const note = iconText(scene, vx, cy + 1, r.note, { ...TEXT, fontSize: '13px', color: '#2f7a2f', fontStyle: 'italic' }).setOrigin(1, 0.5);
        body.add(note);
        vx -= note.width + 8;
      }
      body.add(iconText(scene, vx, cy, r.value, { ...TEXT, fontStyle: 'bold' }).setOrigin(1, 0.5));
      if (i < shown.length - 1) {                       // dashed divider
        const g = scene.add.graphics().lineStyle(1, 0xe2c89a, 1);
        for (let dx = left + 12; dx < right - 12; dx += 8) g.lineBetween(dx, cy + ROW_H / 2, dx + 4, cy + ROW_H / 2);
        body.add(g);
      }
    });
    if (rows.length > VISIBLE) {
      body.add(iconText(scene, right - 10, cardTop + cardH - 4, `${offset + 1}–${offset + shown.length} / ${rows.length}`,
        { ...TEXT, fontSize: '12px', color: '#8a7a5c' }).setOrigin(1, 1));
    }
    if (since) {
      body.add(iconText(scene, x, cardTop + cardH + 16, SINCE_NOTE, { ...TEXT, fontSize: '13px', color: '#8a7a5c' }).setOrigin(0.5));
    }
  };

  // A long tab scrolls by whole rows: the wheel over the panel, or a drag on the card.
  const scrollBy = (n) => {
    if (!tabs) return;
    const max = Math.max(0, tabs[active].rows.length - VISIBLE);
    const next = Phaser.Math.Clamp(offset + n, 0, max);
    if (next !== offset) { offset = next; renderBody(); }
  };
  const onWheel = (_p, _over, _dx, dy) => { if (!closed) scrollBy(Math.sign(dy)); };
  scene.input.on('wheel', onWheel);
  layer.once('destroy', () => scene.input.off('wheel', onWheel));
  let dragY = null;
  panel.on('pointerdown', (p) => { dragY = p.y; });
  panel.on('pointermove', (p) => {
    if (dragY === null || !p.isDown) return;
    const steps = Math.trunc((dragY - p.y) / ROW_H);
    if (steps) { dragY -= steps * ROW_H; scrollBy(steps); }
  });
  panel.on('pointerup', () => { dragY = null; });

  const shut = gameButton(scene, x, top + H - 44, 200, 48, 'Zamknij', () => close(), { variant: 'wood', fontSize: 18 });
  layer.add(shut);

  renderTabs();
  message('Wczytuję…');
  api.stats()
    .then((payload) => { if (closed) return; tabs = statsRows(payload); renderBody(); })
    .catch(() => { if (!closed) message('Nie udało się wczytać statystyk'); });

  // The box takes in the title plank, which sits 24 px above the panel, or a phone clips it.
  enlargeForTouch(scene, layer, { x, y: y - 12, w: W, h: H + 24 }, { overlay });
}
